import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';
import type { SmartPasteGap, SmartPasteGapLevel } from './smartPasteGaps';

interface SmartPasteGapPanelProps {
  gaps: SmartPasteGap[];
  onDismiss: () => void;
}

const TONE: Record<SmartPasteGapLevel, { title: string; row: string; label: string; hint: string }> =
  {
    missing: {
      title: 'text-red-800',
      row: 'border-red-200 bg-red-50',
      label: 'text-red-900',
      hint: 'text-red-700',
    },
    check: {
      title: 'text-amber-800',
      row: 'border-amber-200 bg-amber-50',
      label: 'text-amber-900',
      hint: 'text-amber-700',
    },
  };

const GapRow = ({ gap }: { gap: SmartPasteGap }) => {
  const tone = TONE[gap.level];
  return (
    <li className={`flex items-start gap-3 rounded-xl border border-solid px-3 py-2.5 ${tone.row}`}>
      <span
        aria-hidden
        className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${gap.level === 'missing' ? 'bg-red-600' : 'bg-amber-500'}`}
      />
      <span className='min-w-0 flex-1'>
        <span className={`block text-[13.5px] font-bold ${tone.label}`}>{gap.label}</span>
        <span className={`mt-0.5 block text-[12.5px] ${tone.hint}`}>{gap.hint}</span>
      </span>
    </li>
  );
};

/**
 * "We read your message — here's what we couldn't fill in."
 *
 * Leads with what worked, then names each gap as its own row: the field's label
 * exactly as the form labels it, plus what to do about it. A flat bullet list of
 * sentences left merchants re-reading prose to work out which box to go and fix.
 *
 * Sits next to the form rather than inside the paste modal, because the modal
 * closes the moment the fill succeeds — anything shown there would be gone
 * before the merchant could act on it.
 */
const SmartPasteGapPanel = ({ gaps, onDismiss }: SmartPasteGapPanelProps) => {
  const { t } = useTranslation();

  if (!gaps.length) return null;

  const missing = gaps.filter((gap) => gap.level === 'missing');
  const checks = gaps.filter((gap) => gap.level === 'check');

  const group = (title: string, rows: SmartPasteGap[]) => (
    <div>
      <p className={`m-0 mb-2 text-[13px] font-bold ${TONE[rows[0].level].title}`}>{title}</p>
      <ul className='m-0 flex list-none flex-col gap-2 p-0'>
        {rows.map((gap) => (
          <GapRow key={`${gap.label}-${gap.hint}`} gap={gap} />
        ))}
      </ul>
    </div>
  );

  return (
    <div
      role='status'
      data-testid='smart-paste-notices'
      className='mb-6 overflow-hidden rounded-2xl border border-solid border-sd-border bg-sd-card'
    >
      {/* Green tick first. The fill DID work — burying that under a red list
          reads as "Smart Paste failed", which is the opposite of what happened. */}
      <div className='flex items-center gap-3 px-4 py-3'>
        <span className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-500'>
          <Check className='h-[18px] w-[18px] text-white' strokeWidth={3} />
        </span>
        <div className='min-w-0 flex-1'>
          <p className='m-0 text-[15px] font-bold text-sd-foreground'>
            {String(t('smart_paste_panel_title'))}
          </p>
          <p className='m-0 mt-0.5 text-[12.5px] text-sd-muted-foreground'>
            {String(t('smart_paste_panel_subtitle'))}
          </p>
        </div>
        <button
          type='button'
          onClick={onDismiss}
          aria-label={String(t('close'))}
          className='shrink-0 cursor-pointer rounded-md border-0 bg-transparent p-1 text-sd-muted-foreground transition-colors hover:bg-sd-muted'
        >
          <X className='h-4 w-4' strokeWidth={2.2} />
        </button>
      </div>

      <div className='h-px bg-sd-border' />

      <div className='flex flex-col gap-4 px-4 py-3'>
        {missing.length ? group(String(t('smart_paste_gaps_title')), missing) : null}
        {checks.length ? group(String(t('smart_paste_checks_title')), checks) : null}
      </div>
    </div>
  );
};

export default SmartPasteGapPanel;
